import { useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { CONSTANTS } from "constants/constants";
import VerticalRegistry from "../../constants/verticalRegistry";
import HomeComponent from "../../components/Home/Home";
import routes from "../../constants/routes";
import { useSearchUser } from "../../hooks/useSearchUser";
import searchUserResponseAdapter from "../../adapters/searchUserAdapter";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import MultipleStudentProfile from "../../components/StudentDetails/MultipleStudentProfiles";

const PHONE_SECRET = process.env.REACT_APP_PHONE_SECRET || "il-support-secret";

const Home = () => {
  const navigate = useNavigate();
  const { mutateAsync } = useSearchUser();

  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });

  const [profilesModal, setProfilesModal] = useState({
    open: false,
    profiles: [],
    searchValues: null,
  });

  const searchSchema = z.object({
    vertical: z.string().min(1, "Vertical is required"),

    phone: z
      .string()
      .refine(
        (val) => val === "" || /^\d+$/.test(val),
        "Only numbers are allowed",
      )
      .refine(
        (val) => val === "" || val.length >= 7,
        "Phone number must be at least 7 digits",
      )
      .optional(),

    uam_id: z.string().regex(/^\d*$/, "Only integers are allowed").optional(),

    admission_number: z
      .string()
      .refine(
        (val) => val === "" || val.length >= 2,
        "Minimum 2 characters required",
      )
      .optional(),
  });

  const handleProfileSelect = (selectedProfile, searchValues) => {
    const { account, profile } = selectedProfile;
    const adaptedData = searchUserResponseAdapter(account, profile);

    let queryParams = new URLSearchParams();
    queryParams.append("vertical", searchValues.vertical);

    if (searchValues.phone) {
      const encrypted = CryptoJS.AES.encrypt(
        searchValues.phone,
        PHONE_SECRET,
      ).toString();
      queryParams.append("phone", encrypted);
    }
    if (searchValues.uam_id) {
      queryParams.append("uam_id", searchValues.uam_id);
    }
    if (searchValues.admission_number) {
      queryParams.append("admission_number", searchValues.admission_number);
    }

    navigate(`${routes.studentDetails}?${queryParams.toString()}`, {
      state: {
        studentData: adaptedData,
        vertical: searchValues.vertical,
        phone: account.phone || searchValues.phone,
      },
    });

    setProfilesModal({ open: false, profiles: [], searchValues: null });
  };

  const formik = useFormik({
    initialValues: {
      vertical: "IL",
      admission_number: "",
      phone: "",
      uam_id: "",
    },

    validationSchema: toFormikValidationSchema(searchSchema),

    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,

    validate: (values) => {
      const errors = {};

      if (!values.vertical) {
        errors.vertical = "Vertical is required";
        return errors;
      }

      const verticalObj = VerticalRegistry.getVerticalByName(values.vertical);
      const config = verticalObj?.searchConfig;

      if (!config) return errors;

      const allowedFields = Object.keys(config).filter((key) => config[key]);

      const isAnyFilled = allowedFields.some(
        (field) => values[field]?.trim() !== "",
      );

      if (!isAnyFilled) {
        errors.general = "Enter at least one search parameter";
      }

      return errors;
    },

    onSubmit: async (values, { setSubmitting }) => {
      try {
        const response = await mutateAsync({
          vertical: values.vertical,
          searchParams: values,
        });

        const verticalObj = VerticalRegistry.getVerticalByName(values.vertical);
        const targetVerticalId = verticalObj?.id;

        const allProfiles = [];

        if (response?.data?.accounts) {
          response.data.accounts.forEach((account) => {
            if (account.profiles) {
              account.profiles.forEach((profile) => {
                if (profile.verticalId === targetVerticalId) {
                  allProfiles.push({ account, profile });
                }
              });
            }
          });
        }

        if (allProfiles.length === CONSTANTS.ONE) {
          handleProfileSelect(allProfiles[0], values);
        } else if (allProfiles.length > CONSTANTS.ONE) {
          setProfilesModal({
            open: true,
            profiles: allProfiles,
            searchValues: values,
          });
        } else {
          setErrorSnackbar({
            open: true,
            message:
              "No user found with the provided details for the selected vertical.",
          });
        }
      } catch (error) {
        setErrorSnackbar({
          open: true,
          message: error?.message || "Failed to search user. Please try again.",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <HomeComponent formik={formik} />

      <MultipleStudentProfile
        open={profilesModal.open}
        onClose={() =>
          setProfilesModal({ open: false, profiles: [], searchValues: null })
        }
        profiles={profilesModal.profiles}
        onSelectProfile={(row) =>
          handleProfileSelect(row, profilesModal.searchValues)
        }
      />

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default Home;
